import { create } from "zustand";
import { persist } from "zustand/middleware";
import { normalizeFilterOptions } from "../services/filter-normalizer";
import type { RawFilterOptions } from "../services/filter-normalizer";

/**
 * 🎯 InstantSearch Filter Store
 *
 * NavState + Persisted State + Deeplink Guard (Hybrid) mimarisi
 *
 * Store Responsibility:
 * - Filter state management (persisted)
 * - NavState integration (location.state)
 * - Deeplink guard (clearFilters when no location.state)
 * - Filter persistence across page refreshes
 */

export interface PriceRange {
  min: number | null;
  max: number | null;
}

export interface FilterOptions {
  // Brand filters (id = Typesense filter, name = badge)
  "brand.id"?: string[];
  "brand.name"?: string[];
  // Category filters (id = Typesense filter, name = badge)
  "categories.categoryId"?: string[];
  "categories.name"?: string[];
  categoryId?: string[]; // legacy root categoryId — normalizer categories.categoryId'e taşır
  // Merchant filters
  "merchant.name"?: string[];
  // Seller filters
  "seller.id"?: string[]; // Typesense filtreleme için (seller.id field)
  "seller.name"?: string[]; // Badge gösterimi için (display value)
  // Stock filters
  inStock?: string[];
  // Axis filters (variant attributes)
  axis1Name?: string[];
  axis1Value?: string[];
  axis2Name?: string[];
  axis2Value?: string[];
  // Attribute filters
  "attributes.attributeValueId"?: string[]; // VectorSearch final_response attribute filtreleri
  "attributes.name"?: string[];
  "attributes.value"?: string[];
  // Generic filter support
  [key: string]: string[] | undefined;
}

interface FilterStoreState {
  // 🗄️ Filter State
  filters: FilterOptions;
  priceRange: PriceRange; // 🆕 Price range filter
  discountOnly: boolean; // 🆕 Discounts only (indirimdekiler)

  // 🔧 Actions

  /**
   * Filter'ları set et (persist'e yaz).
   * Backend {id,name}[] formatı ve eski string[] formatı normalize edilir.
   */
  setFilters: (filters: FilterOptions | RawFilterOptions) => void;

  /**
   * Filter'ları temizle (deeplink guard için)
   */
  clearFilters: () => void;

  /**
   * Belirli bir filter key'ini güncelle
   */
  updateFilter: (key: string, values: string[]) => void;

  /**
   * Belirli bir filter key'ini kaldır
   */
  removeFilter: (key: string) => void;

  /**
   * 🆕 Price range set et
   */
  setPriceRange: (min: number | null, max: number | null) => void;

  /**
   * 🆕 Price range temizle
   */
  clearPriceRange: () => void;

  /**
   * 🆕 İndirimdekiler filtresi
   */
  setDiscountOnly: (enabled: boolean) => void;
  toggleDiscountOnly: () => void;

  /**
   * Store'u sıfırla
   */
  reset: () => void;
}

const initialState: Omit<
  FilterStoreState,
  | "setFilters"
  | "clearFilters"
  | "updateFilter"
  | "removeFilter"
  | "setPriceRange"
  | "clearPriceRange"
  | "setDiscountOnly"
  | "toggleDiscountOnly"
  | "reset"
> = {
  filters: {},
  priceRange: { min: null, max: null }, // 🆕
  discountOnly: false, // 🆕
};

export const useFilterStore = create<FilterStoreState>()(
  persist(
    (set) => ({
      ...initialState,

      // 🔧 Filter Actions

      setFilters: (filters: FilterOptions | RawFilterOptions) =>
        set({
          filters: normalizeFilterOptions(filters),
        }),

      clearFilters: () =>
        set({
          filters: {},
          priceRange: { min: null, max: null }, // 🆕 Price range de temizle
          discountOnly: false, // 🆕
        }),

      updateFilter: (key: string, values: string[]) =>
        set((state) => ({
          filters: {
            ...state.filters,
            [key]: values.length > 0 ? values : undefined,
          },
        })),

      removeFilter: (key: string) =>
        set((state) => {
          const newFilters = { ...state.filters };
          delete newFilters[key];
          return { filters: newFilters };
        }),

      setPriceRange: (min: number | null, max: number | null) =>
        set({ priceRange: { min, max } }),

      clearPriceRange: () =>
        set({ priceRange: { min: null, max: null } }),

      setDiscountOnly: (enabled: boolean) => set({ discountOnly: enabled }),

      toggleDiscountOnly: () =>
        set((state) => ({ discountOnly: !state.discountOnly })),

      reset: () =>
        set({
          ...initialState,
        }),
    }),
    {
      name: "instantsearch-filter-storage",
      // Filters'ı persist et
      partialize: (state) => ({
        filters: state.filters,
        priceRange: state.priceRange, // 🆕 Price range de persist et
        discountOnly: state.discountOnly, // 🆕
      }),
    }
  )
);
